import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { classNames } from '../helper'

/**
 * Tomato – Flow Working Session Timer
 *
 * Jeden Dienstag 09:40–11:40 Uhr. Ablauf: 25 min Fokus / 5 min Pause,
 * viermal hintereinander. Der Timer richtet sich nach der echten Uhrzeit –
 * wer 5 min später kommt, hat die 5 min schon verloren.
 *
 * Gongs signalisieren Start und Ende jeder Pause (Web Audio, kein Asset noetig).
 * Kein Login noetig – die Seite liegt unter /tomato.
 */

const TUESDAY = 2 // Date.getDay(): 0 = So
const START_HOUR = 9
const START_MIN = 40
const MIN = 60

// Ablauf ab Session-Start (Sekunden je Phase)
const PHASES = [
  { key: 'focus1', type: 'focus', label: 'Fokus 1', dur: 25 * MIN },
  { key: 'break1', type: 'break', label: 'Pause 1', dur: 5 * MIN },
  { key: 'focus2', type: 'focus', label: 'Fokus 2', dur: 25 * MIN },
  { key: 'break2', type: 'break', label: 'Pause 2', dur: 5 * MIN },
  { key: 'focus3', type: 'focus', label: 'Fokus 3', dur: 25 * MIN },
  { key: 'break3', type: 'break', label: 'Pause 3', dur: 5 * MIN },
  { key: 'focus4', type: 'focus', label: 'Fokus 4', dur: 25 * MIN },
  { key: 'break4', type: 'break', label: 'Pause 4', dur: 5 * MIN }
]

const TOTAL = PHASES.reduce((sum, p) => sum + p.dur, 0) // 7200s

// Phasengrenzen (Offset in s ab Start) + welcher Gong dazu gehoert
const BOUNDARIES = (() => {
  const list = [{ offset: 0, kind: 'session-start' }]
  let acc = 0
  PHASES.forEach((p, i) => {
    acc += p.dur
    if (i === PHASES.length - 1) list.push({ offset: acc, kind: 'session-end' })
    else
      list.push({
        offset: acc,
        kind: p.type === 'focus' ? 'break-start' : 'break-end'
      })
  })
  return list
})()

function atTime(base, h, m) {
  const d = new Date(base)
  d.setHours(h, m, 0, 0)
  return d
}

function addDays(date, days) {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

// Aktuelle oder naechste Dienstags-Session bestimmen
function getSession(now) {
  const dow = now.getDay()
  const todayStart = atTime(now, START_HOUR, START_MIN)
  const todayEnd = atTime(now, START_HOUR, START_MIN + TOTAL / MIN)

  let add
  if (dow === TUESDAY && now <= todayEnd) add = 0
  else add = (TUESDAY - dow + 7) % 7 || 7

  return {
    start: addDays(todayStart, add),
    end: addDays(todayEnd, add)
  }
}

function getState(now) {
  const { start, end } = getSession(now)
  if (now < start) {
    return { status: 'upcoming', start, end, untilStart: (start - now) / 1000 }
  }
  const elapsed = (now - start) / 1000
  let acc = 0
  for (let i = 0; i < PHASES.length; i++) {
    const p = PHASES[i]
    if (elapsed < acc + p.dur) {
      return {
        status: 'active',
        start,
        end,
        phase: p,
        phaseIndex: i,
        phaseRemaining: acc + p.dur - elapsed,
        phaseElapsed: elapsed - acc,
        totalRemaining: TOTAL - elapsed
      }
    }
    acc += p.dur
  }
  // Sollte dank getSession() nicht passieren
  return { status: 'upcoming', start, end, untilStart: (start - now) / 1000 }
}

function fmt(seconds) {
  const s = Math.max(0, Math.ceil(seconds))
  const m = Math.floor(s / 60)
  const ss = s % 60
  return `${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
}

function fmtClock(date) {
  return date.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Gong per Web Audio synthetisieren – Toene als Frequenzfolge
const GONGS = {
  'session-start': [261.6, 392.0], // C4 + G4, aufmunternd
  'break-start': [196.0], // tiefer, ruhiger Gong -> Pause
  'break-end': [392.0, 523.3], // hoeher, zweistufig -> zurueck an die Arbeit
  'session-end': [523.3, 392.0, 261.6] // absteigend -> Feierabend
}

function playGong(ctx, kind) {
  const tones = GONGS[kind] || GONGS['break-start']
  const t0 = ctx.currentTime
  tones.forEach((freq, i) => {
    const t = t0 + i * 0.22
    ;[1, 2.01].forEach((mult, j) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq * mult
      osc.connect(gain)
      gain.connect(ctx.destination)
      const peak = j === 0 ? 0.5 : 0.18
      gain.gain.setValueAtTime(0.0001, t)
      gain.gain.exponentialRampToValueAtTime(peak, t + 0.012)
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 1.9)
      osc.start(t)
      osc.stop(t + 2)
    })
  })
}

const Tomato = () => {
  const [now, setNow] = useState(() => new Date())
  const [soundOn, setSoundOn] = useState(false)
  const [pulse, setPulse] = useState(0)

  const ctxRef = useRef(null)
  const soundOnRef = useRef(false)
  const lastCheckRef = useRef(new Date())

  // Tick + Gong-Erkennung (nur live ueberschrittene Grenzen, nichts rueckwirkend)
  useEffect(() => {
    const tick = () => {
      const current = new Date()
      const prev = lastCheckRef.current
      const { start } = getSession(current)

      BOUNDARIES.forEach((b) => {
        const bTime = new Date(start.getTime() + b.offset * 1000)
        if (prev < bTime && bTime <= current) {
          setPulse((x) => x + 1)
          if (soundOnRef.current && ctxRef.current)
            playGong(ctxRef.current, b.kind)
        }
      })

      lastCheckRef.current = current
      setNow(current)
    }

    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [])

  const enableSound = () => {
    try {
      if (!ctxRef.current) {
        const AudioCtx = window.AudioContext || window.webkitAudioContext
        ctxRef.current = new AudioCtx()
      }
      if (ctxRef.current.state === 'suspended') ctxRef.current.resume()
      playGong(ctxRef.current, 'session-start')
      soundOnRef.current = true
      setSoundOn(true)
    } catch (e) {
      // Ton nicht verfuegbar – Timer laeuft trotzdem
    }
  }

  const state = useMemo(() => getState(now), [now])
  const isBreak = state.status === 'active' && state.phase.type === 'break'

  // Fortschritt der aktuellen Phase fuer den Ring
  const phaseFrac =
    state.status === 'active'
      ? Math.min(1, state.phaseElapsed / state.phase.dur)
      : 0
  const R = 130
  const C = 2 * Math.PI * R

  const accent = isBreak ? '#29CCC7' : '#4461D4'

  return (
    <div
      className={classNames(
        'min-h-screen w-full flex flex-col items-center justify-center px-4 py-10 transition-colors duration-700',
        isBreak ? 'bg-[#06302e]' : 'bg-darkBlack'
      )}
    >
      {/* Header */}
      <div className='absolute top-6 left-0 right-0 flex items-center justify-between px-6'>
        <Link
          to='/'
          className='text-sm font-medium text-white/50 hover:text-white/90 transition-colors'
        >
          ← Aivy OS
        </Link>
        <span className='text-sm font-semibold tracking-wide text-white/60'>
          🍅 Flow Working Session
        </span>
        <button
          onClick={enableSound}
          className={classNames(
            'text-sm font-medium rounded-full px-3 py-1 transition-colors',
            soundOn
              ? 'text-white/50 hover:text-white/90'
              : 'bg-white/10 text-white hover:bg-white/20'
          )}
        >
          {soundOn ? '🔔 Ton an' : '🔕 Ton aktivieren'}
        </button>
      </div>

      {state.status === 'upcoming' ? (
        <div className='text-center'>
          <p className='text-white/50 text-sm uppercase tracking-widest mb-4'>
            Nächste Session
          </p>
          <p className='text-5xl sm:text-6xl font-bold text-white tabular-nums'>
            {fmt(state.untilStart)}
          </p>
          <p className='mt-6 text-white/60'>
            {state.start.toLocaleDateString('de-DE', {
              weekday: 'long',
              day: '2-digit',
              month: 'long'
            })}{' '}
            · {fmtClock(state.start)}–{fmtClock(state.end)} Uhr
          </p>
        </div>
      ) : (
        <div className='flex flex-col items-center'>
          {/* Ring + Countdown */}
          <div
            className={classNames(
              'relative transition-transform duration-300',
              pulse ? 'scale-100' : ''
            )}
            key={pulse} // re-mount fuer kurzen Puls beim Phasenwechsel
          >
            <svg
              width='320'
              height='320'
              viewBox='0 0 320 320'
              className='-rotate-90'
            >
              <circle
                cx='160'
                cy='160'
                r={R}
                fill='none'
                stroke='rgba(255,255,255,0.08)'
                strokeWidth='14'
              />
              <circle
                cx='160'
                cy='160'
                r={R}
                fill='none'
                stroke={accent}
                strokeWidth='14'
                strokeLinecap='round'
                strokeDasharray={C}
                strokeDashoffset={C * (1 - phaseFrac)}
                style={{ transition: 'stroke-dashoffset 1s linear' }}
              />
            </svg>
            <div className='absolute inset-0 flex flex-col items-center justify-center'>
              <span
                className='text-sm font-semibold uppercase tracking-widest mb-1'
                style={{ color: accent }}
              >
                {state.phase.label}
              </span>
              <span className='text-6xl sm:text-7xl font-bold text-white tabular-nums'>
                {fmt(state.phaseRemaining)}
              </span>
              <span className='text-white/40 text-sm mt-2'>
                {isBreak ? 'Pause – kurz durchatmen' : 'Fokus – Kopf runter'}
              </span>
            </div>
          </div>

          {/* Phasen-Leiste */}
          <div className='mt-10 flex items-center gap-2'>
            {PHASES.map((p, i) => {
              const done = i < state.phaseIndex
              const active = i === state.phaseIndex
              return (
                <div
                  key={p.key}
                  className='flex flex-col items-center gap-1.5'
                  title={p.label}
                >
                  <div
                    className={classNames(
                      'h-1.5 rounded-full transition-all duration-500',
                      p.type === 'break' ? 'w-6' : 'w-16'
                    )}
                    style={{
                      backgroundColor: done
                        ? 'rgba(255,255,255,0.35)'
                        : active
                          ? accent
                          : 'rgba(255,255,255,0.12)'
                    }}
                  />
                </div>
              )
            })}
          </div>

          <p className='mt-6 text-white/50 text-sm'>
            Session endet {fmtClock(state.end)} Uhr · noch{' '}
            {Math.ceil(state.totalRemaining / 60)} min
          </p>
        </div>
      )}

      {!soundOn && (
        <p className='absolute bottom-6 text-white/30 text-xs text-center px-6'>
          Tipp: „Ton aktivieren“ klicken, damit die Gongs zu Pausen-Start und
          -Ende erklingen.
        </p>
      )}
    </div>
  )
}

export default Tomato
